import { useRef, useState, useCallback, useMemo } from "react";
import { Tree as ArboristTree } from "react-arborist";
import type { TreeApi } from "react-arborist";
import { cn } from "src/shadcn/lib/utils";
import type { TreeProps, TreeNodeData } from "./types";
import {
  DEFAULT_ROW_HEIGHT,
  DEFAULT_INDENT,
  DEFAULT_HEIGHT,
} from "./constants";
import { TreeContext, type TreeContextValue } from "./TreeContext";
import { useTreeExpansion } from "./hooks/useTreeExpansion";
import { useTreeSelection } from "./hooks/useTreeSelection";
import { useTreeCheck } from "./hooks/useTreeCheck";
import { TreeNode } from "./components/TreeNode";
import { countAllNodes } from "./utils/adapter";

export const Tree = ({
  treeData,
  selectable = true,
  selectedKeys,
  defaultSelectedKeys,
  multiple = false,
  onSelect,
  expandedKeys,
  defaultExpandedKeys,
  autoExpandParent = false,
  onExpand,
  checkable = false,
  checkedKeys,
  defaultCheckedKeys,
  checkStrictly = false,
  onCheck,
  draggable = false,
  allowDrop = true,
  onMove,
  titleRender,
  renderNode,
  switcherIcon,
  filterTreeNode,
  loadData,
  loadedKeys,
  onLoad,
  showLine = false,
  blockNode = false,
  height,
  virtual = true,
  disabled = false,
  onRightClick,
  className,
  rowHeight = DEFAULT_ROW_HEIGHT,
  indent = DEFAULT_INDENT,
}: TreeProps) => {
  const treeRef = useRef<TreeApi<TreeNodeData> | undefined>(undefined);
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const loadingKeysRef = useRef(loadingKeys);
  loadingKeysRef.current = loadingKeys;
  const loadedSet = useMemo(() => new Set(loadedKeys ?? []), [loadedKeys]);

  const { initialOpenState, handleToggle } = useTreeExpansion({
    expandedKeys,
    defaultExpandedKeys,
    autoExpandParent,
    onExpand,
    treeRef,
  });

  const { handleSelect } = useTreeSelection({
    selectedKeys,
    defaultSelectedKeys,
    onSelect,
    treeRef,
    selectable,
  });

  const {
    checkedKeys: checkedSet,
    halfCheckedKeys: halfSet,
    handleCheckToggle,
  } = useTreeCheck({
    checkable,
    checkedKeys,
    defaultCheckedKeys,
    checkStrictly,
    treeData,
    onCheck,
  });

  const handleAsyncToggle = useCallback(
    (id: string) => {
      handleToggle(id);
      if (!loadData || !treeRef.current) return;
      const node = treeRef.current.get(id);
      if (!node?.isOpen || loadedSet.has(id) || loadingKeysRef.current.has(id))
        return;
      if (node.data.children?.length) return;

      setLoadingKeys(prev => new Set([...prev, id]));
      loadData(node.data)
        .then(() => {
          onLoad?.([...loadedSet, id], { node: node.data });
        })
        .finally(() => {
          setLoadingKeys(prev => {
            const next = new Set(prev);
            next.delete(id);

            return next;
          });
        });
    },
    [handleToggle, loadData, loadedSet, onLoad]
  );

  const resolvedHeight = useMemo(() => {
    if (height) return height;
    if (!virtual) return countAllNodes(treeData) * rowHeight;

    return DEFAULT_HEIGHT;
  }, [height, virtual, treeData, rowHeight]);

  const ctxValue: TreeContextValue = useMemo(
    () => ({
      checkable,
      checkedKeys: checkedSet,
      halfCheckedKeys: halfSet,
      onCheckToggle: handleCheckToggle,
      titleRender,
      switcherIcon,
      filterTreeNode,
      showLine,
      blockNode,
      disabled,
      selectable,
      indent,
      loadingKeys,
      hasLoadData: !!loadData,
      onRightClick,
    }),
    [
      checkable,
      checkedSet,
      halfSet,
      handleCheckToggle,
      titleRender,
      switcherIcon,
      filterTreeNode,
      showLine,
      blockNode,
      disabled,
      selectable,
      indent,
      loadData,
      loadingKeys,
      onRightClick,
    ]
  );

  return (
    <TreeContext.Provider value={ctxValue}>
      <div
        className={cn("neeto-atoms-tree", showLine && "relative", className)}
      >
        <ArboristTree<TreeNodeData>
          ref={el => {
            if (el) treeRef.current = el;
          }}
          childrenAccessor="children"
          data={treeData}
          disableDrag={!draggable || disabled}
          disableDrop={!allowDrop || disabled}
          disableEdit={true}
          disableMultiSelection={!multiple}
          height={resolvedHeight}
          idAccessor="key"
          indent={indent}
          initialOpenState={initialOpenState}
          openByDefault={false}
          rowHeight={rowHeight}
          selectionFollowsFocus={false}
          width="100%"
          onMove={onMove}
          onSelect={handleSelect}
          onToggle={handleAsyncToggle}
        >
          {renderNode ?? TreeNode}
        </ArboristTree>
      </div>
    </TreeContext.Provider>
  );
};
